import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import debounce from "lodash/debounce";
import { useParams, useLocation } from "react-router-dom";
import { useKeycloakToken } from '../common/useKeycloak';
import ConnectionDetails from "./ConnectionDetails";
import { addConnectionDestinations, deleteRouteConfiguration, getConnectionApiKey, getConnectionDetails, getConnectionLog, getIntegrationDetails, getLogDetails, getRouteConfiguration, testConnectionAction, updateConnection, updateRouteConfiguration } from "../../api/connections/connectionsApi";
import TransformationRulesPanel from "./transformationRules/TransformationRulesPanel";
import {
    FieldErrors,
    FieldMapping,
    FieldMappingsConfig,
    STREAM_TYPES,
    StreamType,
    buildConfigWithRule,
    buildConfigWithoutRule,
    getRuleForDestination,
    parseServerErrors,
} from "./transformationRules/fieldMappingTypes";
import { Connection, ConnectionConfiguration, ConnectionLog, Destination, DestinationType, Member, Organization, Provider, Source, buildConnection, buildDestination, buildMember, buildProvider } from "../common/ObjectTypes";
import { getDestinationDetails, getDestinationsList } from "../../api/destinations/destinationsApi";
import { getSourcesForConnection } from "../../api/sources/sourcesApi";
import { useNavigate } from 'react-router-dom';
import { toast } from "react-toastify";
import Ajv from 'ajv';
import addFormats from 'ajv-formats';
import addKeywords from 'ajv-keywords';
import { loadAllOrganizations, loadOrganizations } from "../../api/organizations/loadOrganizations";
import { filter_page_size, page_size } from "../../api/apiUtils";
import { filterLogDate, filterLogStatus, filterLogType, logGlobalSearch, parseCursor, resetLogFilters } from "../activityLogs/ActivityLogsHelperFunctions";
import { parseDuration } from "../../utils/parseDuration";
import { changeDestinationGlobalSearch, filterDestinationOwner } from "../destinations/DestinationHelperFunctions";
import { changeSourceGlobalSearch } from "../sources/SourceHelperFunctions";
import loadDestinationUrls from '../../api/destinations/loadDestinationUrls';
import { loadDestinationTypes } from '../../api/destinations/loadDestinationTypes';
import ConnectionDetailsOverview from "./ConnectionDetailsOverview";
import ConnectionDetailsProvider from "./ConnectionDetailsProvider";
import ConnectionDetailsDestination from "./ConnectionDetailsDestination";
import ConnectionDetailsSource from "./ConnectionDetailsSources";
import ConnectionDetailsLog from "./ConnectionDetailsLog";
import ConnectionDetailsWebhook from "./ConnectionDetailsWebhook";
import CancelChangesConfirmationModal from "./CancelChangesConfirmationModal";
import getUserPermissions from "../../api/users/getUserPermissions";
import { useTranslation } from "react-i18next";
import { useHelp, HelpCard } from "../../context/HelpContext";
import { getConnectionKind, providerHasPushAction } from "../common/connectionKind";
import { useKeycloak } from "@react-keycloak/web";
import { trackConnectionEnabled, trackConnectionDisabled, trackDestinationAdded } from '../../analytics/analytics';
import { getMyDetails } from "../../api/users/usersApi";
import { getOrganizationMembers } from "../../api/organizations/organizationsApi";
import { loadCurrentUserDetails } from "../organizations/loadCurrentUserDetails";
import ConnectionProgressIndicator from "./NewConnectionProgressIndicator/ConnectionProgressIndicator";
import { skeletonClasses, connectionDetailsClasses } from "../common/tailwindClassnames";

const GENERAL_TAB_SKELETON_ROWS = [
    { label: "w-32", desc: "w-52" },
    { label: "w-24", desc: "w-44" },
];

export function ManageConnectionDetails() {
    type FilterTags = {
        name: string;
        id: string;
    };
    type FilterTypes = {
        base_url__in: FilterTags[];
        enabled: FilterTags[];
        type__in: FilterTags[];
        owner__in: FilterTags[];
        ordering: string;
        search: string;
    };
    type SourceFilterTypes = {
        destination_url__in: FilterTags[];
        enabled: FilterTags[];
        type__in: FilterTags[];
        owner__in: FilterTags[];
        ordering: string;
        search: string;
    };
    type LogFilterTypes = {
        log_level: FilterTags[];
        log_type: FilterTags[];
        from_date: FilterTags[];
        to_date: FilterTags[];
        search: string;
        ordering: string;
    };

    const { t, i18n } = useTranslation();
    const { setHelpContent, closePanel: closeHelpPanel, setRightOffsetPx: setHelpOffsetPx } = useHelp();
    const { connection_id, subpage } = useParams();
    const effectiveSubpage = subpage || "overview";
    const [token, showLoginModal, setShowLoginModal] = useKeycloakToken();
    const { keycloak, initialized } = useKeycloak();
    const location = useLocation();
    const [currentMemberIsAdmin, setCurrentMemberIsAdmin] = useState<boolean>(false);
    const [isFromAddConnectionFlow, setIsFromAddConnectionFlow] = useState(false);
    const [skippedAddingDestinations, setSkippedAddingDestinations] = useState(false);
    const [canAddDestination, setCanAddDestination] = useState(false);
    const [pageLoaded, setPageLoaded] = useState(false);
    const [connection, setConnection] = useState<Connection>(buildConnection());
    const [connectionHasDestinations, setConnectionHasDestinations] = useState(() => connection.destinations.length > 0);
    const [showQuickStart, setShowQuickStart] = useState(false);
    const [quickStartSuccessful, setQuickStartSuccessful] = useState(false);
    const quickStartInitialized = useRef(false);
    const sourcesTabInitialized = useRef(false);
    const filterDataInitialized = useRef(false);
    const [reloadConnection, setReloadConnection] = useState(true);
    const [reloadConnectionOnly, setReloadConnectionOnly] = useState(false);
    const [apiKey, setApiKey] = useState("");
    const [connectionProvider, setConnectionProvider] = useState<Provider>(buildProvider());
    const [updatedConfigurations, setUpdatedConfigurations] = useState<any[]>([]);
    const [destinationFilter, setDestinationFilter] = useState<FilterTypes>({
        base_url__in: [],
        enabled: [],
        type__in: [],
        owner__in: [],
        ordering: "",
        search: ""
    });
    const [sourceFilter, setSourceFilter] = useState<SourceFilterTypes>(() => {
        const savedFilter = sessionStorage.getItem('connectionSourceFilter');
        return savedFilter ? JSON.parse(savedFilter) : {
            destination_url__in: [],
            enabled: [],
            type__in: [],
            owner__in: [],
            ordering: "",
            search: ""
        }
    });
    const [logsFilter, setLogsFilter] = useState<LogFilterTypes>(() => {
        const savedFilter = sessionStorage.getItem('connectionLogsFilter');

        if (savedFilter) {
            return JSON.parse(savedFilter);
        } else {
            const threeHoursAgo = new Date();
            threeHoursAgo.setHours(threeHoursAgo.getHours() - 3);

            const formattedDate = threeHoursAgo;

            return {
                log_type: [],
                log_level: [],
                from_date: [{
                    name: t("Start Date & Time"),
                    id: formattedDate.toISOString(),
                    attribute_name: "datetime"
                }],
                to_date: [],
                search: "",
                ordering: "-created_at"
            }
        }
    })
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [destDetailsModal, setDestDetailsModal] = useState(false);
    /* 
        CONNECTION DESTINATIONS:
            - "destinations" array is for destinations already associated with the connection
            - "existingDestPhase0" is for existing destinations that are available to add and unselected
            - "existingDestPhase1" is for existing destinations that have been checked and selected to add (yet to be sorted)
            - "existingDestPhase2" is for selected destinations that are sorted to the top of the available list so they remain there
            regardless of search/filter results
            - "existingDestPhase3" is for unselected destination that are still in their sorted position at the top of the available list
    */
    const [destinations, setDestinations] = useState<Destination[]>([]);
    const [existingDestPhase0, setExistingDestPhase0] = useState<Destination[]>([]);
    const [existingDestPhase1, setExistingDestPhase1] = useState<Destination[]>([]);
    const [existingDestPhase2, setExistingDestPhase2] = useState<Destination[]>([]);
    const [existingDestPhase3, setExistingDestPhase3] = useState<Destination[]>([]);
    const [reloadDestinations, setReloadDestinations] = useState(false);

    // Transformation Rules side panel state
    const [rulesPanelOpen, setRulesPanelOpen] = useState(false);
    const [rulesPanelDestination, setRulesPanelDestination] = useState<Destination | null>(null);
    const [routeConfigData, setRouteConfigData] = useState<FieldMappingsConfig>({ field_mappings: {} });
    // route_configuration metadata, cached separately from field_mappings so we can PATCH
    // the existing config (avoiding a duplicate on every save) and DELETE it cleanly when
    // the last rule is removed.
    const [routeConfigurationId, setRouteConfigurationId] = useState<string | null>(null);
    const [routeConfigurationName, setRouteConfigurationName] = useState<string>("");

    const [reloadLogs, setReloadLogs] = useState(false);
    const [loadingLogs, setLoadingLogs] = useState(false);
    const [isAppendingLogs, setIsAppendingLogs] = useState(false);
    const [logsError, setLogsError] = useState(false);
    const currentRequestRef = useRef<AbortController | null>(null);
    const defaultTimezone = { label: "UTC", value: "+0000" };
    const [selectedTimezone, setSelectedTimezone] = useState<any>(() => {
        // `selectedTimezone` is stored as JSON. If we accidentally keep it as a raw string,
        // `selectedTimezone.value` becomes `undefined` and we end up appending "undefined" to dates.
        const stored = localStorage.getItem("selectedTimezone");
        if (!stored) return defaultTimezone;
        try {
            const parsed = JSON.parse(stored);
            return parsed && typeof parsed === "object" && typeof parsed.value === "string" ? parsed : defaultTimezone;
        } catch {
            return defaultTimezone;
        }
    });
    const [logs, setLogs] = useState<ConnectionLog[]>([]);
    const destinationOffset = 0;
    const [nextLogsCursor, setNextLogsCursor] = useState<string | null>(null);
    const [prevLogsCursor, setPrevLogsCursor] = useState<string | null>(null);
    const [selectedCursor, setSelectedCursor] = useState<string | null>(null);

    const debouncedSearch = useCallback(
        debounce((updatedFilter: LogFilterTypes) => {
            setIsAppendingLogs(false);
            setSelectedCursor(null);
            setReloadLogs(true);
        }, 300),
        []
    );

    const queueFilterSearch = useCallback((updatedFilter: LogFilterTypes) => {
        setLogsFilter(updatedFilter);
        setLoadingLogs(true);

        debouncedSearch(updatedFilter);
    }, [debouncedSearch]);

    useEffect(() => {
        setConnectionHasDestinations(destinations.length > 0);
    }, [destinations]);

    useEffect(() => {
        if (!quickStartInitialized.current) return;
        const sessionKey = `quickStart_${connection_id}`;
        if (connectionHasDestinations && showQuickStart) {
            setQuickStartSuccessful(true);
            sessionStorage.removeItem(sessionKey);
        } else if (!connectionHasDestinations) {
            setShowQuickStart(true);
            setQuickStartSuccessful(false);
            sessionStorage.setItem(sessionKey, 'pending');
        }
    }, [connectionHasDestinations]);

    useEffect(() => {
        if (!pageLoaded || !quickStartInitialized.current || showQuickStart) return;
        const isWebhookConnection = connectionProvider.type != null && getConnectionKind(connectionProvider.type) === 'webhook';
        const webhookConfigured = !!connectionProvider.webhook_configuration;
        if (isWebhookConnection && !webhookConfigured) {
            setShowQuickStart(true);
        }
    }, [connectionProvider.type, connectionProvider.webhook_configuration, pageLoaded, showQuickStart]);

    useEffect(() => {
        const providerType = connectionProvider.type;

        // The connection-rules guide is tied to the Rules panel, not the provider type, so
        // it's built independently of the provider-type guard below.
        const rulesCards: HelpCard[] = rulesPanelOpen ? [{
            title: t('Connection Rules'),
            description: t('Learn how to transform and map data with connection rules.'),
            button: { label: t('Learn More'), url: 'https://support.earthranger.com/connections/connection-rules' },
        }] : [];

        if (!providerType?.id || !providerType?.value) {
            setHelpContent(rulesCards.length > 0 ? { cards: rulesCards } : null);
            return;
        }

        const kind = getConnectionKind(providerType);
        const cards: HelpCard[] = [
            // While the Rules side panel is open, surface the connection-rules guide first.
            ...rulesCards,
            ...(kind === 'webhook' ? [{
                title: t('Gundi Webhook API'),
                description: t('Learn how to send data to Gundi using webhooks.'),
                button: { label: t('Learn More'), url: 'https://support.earthranger.com/developer_docs/custom-webhook' },
            }] : []),
            ...((kind === 'push-only' || providerHasPushAction(providerType)) ? [{
                title: t('Gundi API'),
                description: t('Learn how to push data to Gundi using the API.'),
                button: { label: t('Learn More'), url: 'https://support.earthranger.com/developer_docs/gundi-api' },
            }] : []),
        ];
        setHelpContent(cards.length > 0 ? { cards } : null);
    }, [connectionProvider.type, rulesPanelOpen, i18n.language, t, setHelpContent]);

    useEffect(() => {
        return () => setHelpContent(null);
    }, [setHelpContent]);

    // Coordinate the Help side panel with the Rules side panel across three width ranges:
    //   >= 1400px : both coexist side-by-side — shift Help + "?" left by the Rules width.
    //   768-1400  : not enough room for both, so close Help when Rules opens (see effect below).
    //   < 768px   : the md/hamburger breakpoint — Rules is full-screen and Help stacks
    //               full-screen on top of it, so there's nothing to shift (offset stays 0).
    // The offset is recomputed on resize so dragging across a breakpoint stays consistent.
    useEffect(() => {
        const RULES_PANEL_WIDTH = 520;
        const FULLSCREEN_MAX_WIDTH = 768;
        const applyOffset = () => {
            if (!rulesPanelOpen) {
                setHelpOffsetPx(0);
                return;
            }
            const width = typeof window !== "undefined" ? window.innerWidth : RULES_PANEL_WIDTH;
            setHelpOffsetPx(width < FULLSCREEN_MAX_WIDTH ? 0 : RULES_PANEL_WIDTH);
        };
        applyOffset();
        window.addEventListener("resize", applyOffset);
        return () => {
            window.removeEventListener("resize", applyOffset);
            setHelpOffsetPx(0);
        };
    }, [rulesPanelOpen, setHelpOffsetPx]);

    // One-shot when Rules opens: in the mid range Help can't sit beside it, so close Help and
    // let the user re-open it from the (shifted) "?". Below the full-screen breakpoint they
    // stack instead, so Help is left alone there.
    useEffect(() => {
        if (!rulesPanelOpen) return;
        const COEXIST_MIN_WIDTH = 1400;
        const FULLSCREEN_MAX_WIDTH = 768;
        const width = typeof window !== "undefined" ? window.innerWidth : COEXIST_MIN_WIDTH;
        if (width >= FULLSCREEN_MAX_WIDTH && width < COEXIST_MIN_WIDTH) {
            closeHelpPanel();
        }
    }, [rulesPanelOpen, closeHelpPanel]);

    useEffect(() => {
        return () => {
            if (debouncedSearch) {
                debouncedSearch.cancel();
            }
            if (currentRequestRef.current) {
                currentRequestRef.current.abort();
            }
        };
    }, [debouncedSearch]);

    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [loadingOrganizations, setLoadingOrganizations] = useState(false);

    const [sources, setSources] = useState<Source[]>([]);
    const [totalSources, setTotalSources] = useState<number>(0);
    const [sourcesOffset, setSourcesOffset] = useState<number>(0);
    const [reloadSources, setReloadSources] = useState(false);

    const [destinationUrls, setDestinationUrls] = useState<any[]>([]);
    const [loadingDestinationUrls, setLoadingDestinationUrls] = useState(false);
    const [destinationTypes, setDestinationTypes] = useState<DestinationType[]>([]);
    const [loadingDestinationTypes, setLoadingDestinationTypes] = useState(false);

    const [changesMade, setChangesMade] = useState(false);
    // Remove addDestModal state, not needed for page navigation
    const [addExistingDest, setAddExistingDest] = useState(false);
    const [updatingDestinations, setUpdatingDestinations] = useState(false);
    const [errors, setErrors] = useState({})
    const [rsjfErrors, setRsjfErrors] = useState<any[]>([]);
    const [testActionLoading, setTestActionLoading] = useState(false);
    const [testActionData, setTestActionData] = useState<any>(null);
    const [testActionStatus, setTestActionStatus] = useState<number>(0);
    const navigate = useNavigate();

    const loadDestinationURlsData = async () => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            setLoadingDestinationUrls(true);
            try {
                let accumulated: string[] = [];
                let offset = 0;
                while (true) {
                    const [dest_urls, total, error] = await loadDestinationUrls(token, offset, "", accumulated);
                    if (error) break;
                    accumulated = dest_urls;
                    if (offset + filter_page_size >= total) break;
                    offset += filter_page_size;
                }
                setDestinationUrls([...new Set(accumulated)]);
            } finally {
                setLoadingDestinationUrls(false);
            }
        }
    }

    const loadOrganizationsData = async (search_str: string = "") => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            setLoadingOrganizations(true);
            try {
                if (search_str) {
                    const [orgs, , error] = await loadOrganizations(token, 0, search_str, []);
                    if (!error) setOrganizations(orgs);
                } else {
                    setOrganizations(await loadAllOrganizations(token));
                }
            } finally {
                setLoadingOrganizations(false);
            }
        }
    }

    const loadDestinationTypesData = async () => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            setLoadingDestinationTypes(true);
            try {
                let accumulated: DestinationType[] = [];
                let offset = 0;
                while (true) {
                    const [dest_types, total, error] = await loadDestinationTypes(token, offset, "", accumulated);
                    if (error) break;
                    accumulated = dest_types;
                    if (offset + filter_page_size >= total) break;
                    offset += filter_page_size;
                }
                setDestinationTypes(accumulated);
            } finally {
                setLoadingDestinationTypes(false);
            }
        }
    }

    const loadDestinationsTab = async () => {
        await loadDestinations([...destinations, ...existingDestPhase1, ...existingDestPhase2]);
    };

    useEffect(() => {
        sessionStorage.setItem('connectionSourceFilter', JSON.stringify(sourceFilter));
        const loadPerms = async () => {
            if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
                try {
                    const temp = await getUserPermissions(token);
                    setCanAddDestination(temp);
                } catch (err) {
                    console.log(err)
                }
            }
        }

        if (!pageLoaded) {
            loadApiKey();
            loadPerms();
        }

        if (reloadConnection) loadConnectionDetails();
        if (reloadConnectionOnly && pageLoaded) loadConnectionDestinationsOnly();
        if (reloadDestinations && pageLoaded) loadDestinationsTab();
        if (reloadLogs && destinations.length > 0) loadConnectionLogs(destinations);
        if (reloadSources) loadConnectionSources(connectionProvider.id);
    }, [token, reloadDestinations, reloadLogs, reloadSources, reloadConnection, reloadConnectionOnly, subpage, pageLoaded]);

    useEffect(() => {
        if (subpage === 'logs' && pageLoaded && destinations.length > 0 && !loadingLogs) {
            console.log("Subpage changed to logs, loading logs");
            loadConnectionLogs(destinations);
        }
    }, [subpage, pageLoaded, destinations]);

    // The logs tab is disabled when the connection has no destination, so a direct
    // visit to the logs URL is redirected to the destinations tab. Temporary, pending
    // support for logs on destination-less connections.
    useEffect(() => {
        if (pageLoaded && subpage === 'logs' && !connectionHasDestinations) {
            navigate("/connections/" + connection_id + "/destinations", { replace: true });
        }
    }, [pageLoaded, subpage, connectionHasDestinations, connection_id, navigate]);

    useEffect(() => {
        if (!pageLoaded) return;
        if (effectiveSubpage === 'sources' && !sourcesTabInitialized.current) {
            sourcesTabInitialized.current = true;
            setReloadSources(true);
        }
    }, [pageLoaded, effectiveSubpage]);

    useEffect(() => {
        if (!pageLoaded || filterDataInitialized.current) return;
        filterDataInitialized.current = true;
        Promise.all([
            loadDestinationURlsData(),
            loadDestinationTypesData(),
            loadOrganizationsData()
        ]);
    }, [pageLoaded]);

    useEffect(() => {
        if (location.state && location.state.fromAddConnectionFlow) {
            setIsFromAddConnectionFlow(true);
        }
        if (location.state && location.state.skippedAddingDestinations) {
            setSkippedAddingDestinations(true);
        }
        // If we just navigated back to the destinations subpage, reload both the
        // destinations list and the connection itself so connectionHasDestinations
        // reflects any newly added or removed destinations
        if (subpage === 'destinations') {
            setReloadConnectionOnly(true);
        }
    }, [location, subpage]);

    // LOAD FUNCTIONS
    const loadConnectionDestinationsOnly = async () => {
        const isLoggedIn = keycloak?.authenticated && localStorage.getItem("userAuthorized") !== null;
        if (!isLoggedIn) return;

        if (token && typeof token === 'string' && !keycloak.isTokenExpired() && connection_id) {
            try {
                const tempConnection = await getConnectionDetails(token, connection_id);
                if (!tempConnection || !tempConnection.owner || !tempConnection.owner.id) return;
                setDestinations(tempConnection.destinations);
                setConnection(tempConnection);
                setConnectionHasDestinations(tempConnection.destinations.length > 0);
                setReloadConnectionOnly(false);
                setReloadDestinations(true);
            } catch (err) {
                console.log('Failed to reload connection:', err);
            }
        }
    }

    const loadConnectionDetails = async () => {
        // Check if user is authenticated before making API calls
        const isLoggedIn = keycloak?.authenticated && localStorage.getItem("userAuthorized") !== null;
        if (!isLoggedIn) {
            return;
        }

        if (token && typeof token === 'string' && !keycloak.isTokenExpired() && connection_id) {
            try {
                const tempConnection = await getConnectionDetails(token, connection_id);
                // Check if tempConnection is defined and has required properties
                if (!tempConnection || !tempConnection.owner || !tempConnection.owner.id) {
                    return;
                }
                setDestinations(tempConnection.destinations);
                setConnection(tempConnection);
                setConnectionHasDestinations(tempConnection.destinations.length > 0);
                if (!quickStartInitialized.current) {
                    quickStartInitialized.current = true;
                    const isNewConnectionFlow = !!(location.state && location.state.fromAddConnectionFlow);
                    const hasDestinations = tempConnection.destinations.length > 0;
                    const sessionKey = `quickStart_${connection_id}`;
                    const quickStartPending = sessionStorage.getItem(sessionKey) === 'pending';
                    if (!hasDestinations) {
                        setShowQuickStart(true);
                        sessionStorage.setItem(sessionKey, 'pending');
                    } else if (isNewConnectionFlow || quickStartPending) {
                        setShowQuickStart(true);
                        setQuickStartSuccessful(true);
                        sessionStorage.removeItem(sessionKey);
                    }
                }
                setDestinationFilter(filterDestinationOwner({
                    target: {
                        "name": tempConnection.owner.name,
                        "value": tempConnection.owner.id,
                    }
                }, destinationFilter));
                const savedtz = localStorage.getItem("selectedTimezone");
                setSelectedTimezone(savedtz ? JSON.parse(savedtz) : { label: "UTC", value: "+0000" });
                await Promise.all([
                    setCurrentMemberIsAdmin(await loadCurrentUserDetails(tempConnection.owner.id, token)),
                    loadConnectionProvider(),
                    subpage === "logs" && loadConnectionLogs(tempConnection.destinations),
                ]);
                setReloadConnection(false);
                setPageLoaded(true);
            } catch (err) {
                // Silently handle errors - they're already handled by the interceptor
                console.log('Failed to load connection details:', err);
            }
        }
    }

    const loadApiKey = async () => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired() && connection_id) {
            const tempKey = await getConnectionApiKey(token, connection_id);
            setApiKey(tempKey.api_key);
        }
    }

    // load all destinations, set availableDestinations as all destinations minus the ones already associated with this connection
    const loadDestinations = async (destArray: Destination[]) => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            setExistingDestPhase2(existingDestPhase2.concat(existingDestPhase1));
            setExistingDestPhase3(existingDestPhase2.concat(existingDestPhase1));
            setUpdatingDestinations(true);
            try {
                const destinationFilterQuery = {
                    base_url__in: destinationFilter.base_url__in.map(a => a.id).toString(),
                    enabled: destinationFilter.enabled.map(a => a.id).toString(),
                    type__in: destinationFilter.type__in.map(a => a.id).toString(),
                    owner__in: destinationFilter.owner__in.map(a => a.id).toString(),
                    ordering: destinationFilter.ordering,
                    search: destinationFilter.search,
                    offset: destinationOffset,
                    previous: process.env.REACT_APP_BASE_URL_LOCAL + "/integrations/?pager=limit&limit=" + page_size + "&offset=" + (destinationOffset - page_size),
                    next: process.env.REACT_APP_BASE_URL_LOCAL + "/integrations/?pager=limit&limit=" + page_size + "&offset=" + (destinationOffset + page_size)
                }
                const temp = await getDestinationsList(token, destinationFilterQuery);

                // check to see if destination in temp.result is in the selected array
                const tempDestArray: Destination[] = temp.results.filter((b: Destination) => !destArray.map((a: Destination) => a.id).includes(b.id))

                setExistingDestPhase0(tempDestArray);
                setExistingDestPhase1([]);
                setUpdatingDestinations(false);
                setReloadDestinations(false);
            } catch (err) {
                console.log(err)
            }
        }
    }

    const fetchConnectionLogsWithBackoff = async (
        authToken: string,
        logQuery: any,
        abortSignal: AbortSignal,
        maxRetries = 2,
        requestTimeout = 30000 // 30 second timeout per request
    ) => {
        let attempt = 0;
        let delay = 500;

        // eslint-disable-next-line no-constant-condition
        while (true) {
            try {
                if (abortSignal.aborted) {
                    const abortError: any = new Error("Request aborted");
                    abortError.name = "AbortError";
                    throw abortError;
                }

                const result = await getConnectionLog(authToken, logQuery, abortSignal, requestTimeout);
                return result;
            } catch (err: any) {
                // Handle abort/cancellation errors (don't retry these)
                if (err.name === 'AbortError' ||
                    err.name === 'CanceledError' ||
                    err.code === 'ERR_CANCELED' ||
                    err.code === 'ECONNABORTED' ||
                    abortSignal.aborted) {
                    throw err;
                }

                attempt += 1;
                if (attempt > maxRetries) {
                    throw err;
                }

                await new Promise(resolve => setTimeout(resolve, delay));
                delay *= 2;
                delay = Math.min(delay, 10000); // cap at 10 seconds
            }
        }
    };

    const loadConnectionLogs = async (destination_ids: Destination[]) => {
        console.log("Loading logs");
        setReloadLogs(false);
        setLogsError(false); // Clear any previous error

        if (currentRequestRef.current) {
            console.log("Cancelling previous request");
            currentRequestRef.current.abort();
        }

        const abortController = new AbortController();
        currentRequestRef.current = abortController;

        setLoadingLogs(true);

        let id_array: string[] = destination_ids.map(a => a.id);
        if (token && typeof token === 'string' && !keycloak.isTokenExpired() && connection_id) {
            try {
                id_array.push(connection_id);
                const log_query = {
                    cursor: selectedCursor,
                    integration__in: id_array.toString(),
                    ordering: logsFilter.ordering,
                    log_level: logsFilter.log_level.map(a => a.id).toString(),
                    log_type: logsFilter.log_type.map(a => a.id).toString(),
                    from_date: logsFilter.from_date.length > 0 ? logsFilter.from_date[0].id : "",
                    to_date: logsFilter.to_date.length > 0 ? logsFilter.to_date[0].id : "",
                    search: logsFilter.search
                };

                console.log("Fetching logs with query:", log_query);
                const temp = await fetchConnectionLogsWithBackoff(token, log_query, abortController.signal);
                console.log("Log response received:", temp);

                if (currentRequestRef.current === abortController && !abortController.signal.aborted && temp) {
                    if (selectedCursor === nextLogsCursor && nextLogsCursor !== null) {
                        setLogs(prevLogs => [...prevLogs, ...temp.results]);
                    } else {
                        setLogs(temp.results || []);
                    }

                    const next_cursor = parseCursor(temp.next);
                    const prev_cursor = parseCursor(temp.previous);
                    setNextLogsCursor(next_cursor);
                    setPrevLogsCursor(prev_cursor);
                    setLogsError(false); // Clear error on success
                }
            } catch (err: any) {
                if (err.name === 'AbortError') {
                    console.log("Log request was cancelled");
                    // Don't set error state for aborted requests
                } else {
                    console.error("Error loading logs:", err);
                    if (currentRequestRef.current === abortController) {
                        setLogsError(true); // Set error state for actual failures
                    }
                }
            } finally {
                if (currentRequestRef.current === abortController) {
                    setLoadingLogs(false);
                    setIsAppendingLogs(false);
                }
            }
        } else {
            setLoadingLogs(false);
        }
    }

    const loadConnectionSources = async (provider_id: string) => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            try {
                const sourceQuery = {
                    search: sourceFilter.search,
                    destination_url__in: sourceFilter.destination_url__in.map(a => a.id).toString(),
                    ordering: sourceFilter.ordering,
                    offset: sourcesOffset,
                    previous: process.env.REACT_APP_BASE_URL_LOCAL + "/sources/?pager=limit&limit=" + page_size + "&offset=" + (sourcesOffset - page_size),
                    next: process.env.REACT_APP_BASE_URL_LOCAL + "/sources/?pager=limit&limit=" + page_size + "&offset=" + (sourcesOffset + page_size)
                }
                const temp = await getSourcesForConnection(token, provider_id, sourceQuery);
                setSources(temp.results);
                setTotalSources(temp.count);
                setReloadSources(false);
            } catch (err) {
                console.log(err);
            }
        }
    }

    const loadConnectionProvider = async () => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired() && connection_id) {
            const tempIntegration = await getIntegrationDetails(token, connection_id);
            setConnectionProvider(tempIntegration)
        }
    }

    const loadRouteConfiguration = async () => {
        const route_id = connection.default_route?.id;
        if (!route_id) return;
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            try {
                const route = await getRouteConfiguration(token, route_id);
                const data: FieldMappingsConfig = route?.configuration?.data ?? { field_mappings: {} };
                setRouteConfigData(data);
                setRouteConfigurationId(route?.configuration?.id ?? null);
                setRouteConfigurationName(route?.configuration?.name ?? "");
            } catch (err) {
                console.log('Failed to load route configuration:', err);
            }
        }
    }

    useEffect(() => {
        if (pageLoaded && connection.default_route?.id) {
            loadRouteConfiguration();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageLoaded, connection.default_route?.id, token]);

    // SELECT SUB PAGE ON CONNECTION DETAILS PAGE
    function handleSelectSubPage(e: string) {
        if (e === "logs") {
            setIsAppendingLogs(false);
            setReloadLogs(true);
        }
        navigate("/connections/" + connection_id + "/" + e);
    }

    // HANDLE SAVE CHANGE METHODS
    function handleChangeConnectionName(e: any) {
        setConnectionProvider((prevProvider: Provider) => ({
            ...prevProvider,
            name: e.target.value,
        }))
        setChangesMade(true);
    }

    function handleChangeConnectionOwner(e: any) {
        setConnectionProvider((prevProvider: Provider) => ({
            ...prevProvider,
            owner: { id: e.value, name: e.label, description: "" }
        }));
        setChangesMade(true);
    }

    function handleSearchOrgDropdown(e: any) {
        if (e.length > 1) loadOrganizationsData(e);
    }

    function handleLoadMoreOrgs() {
        loadOrganizationsData();
    }

    function handleChangeProviderEndpoint(e: any) {
        setConnectionProvider((prevProvider: Provider) => ({
            ...prevProvider,
            base_url: e.target.value,
        }))
        setChangesMade(true);
    }

    async function handleToggleProviderEnabled(e: any) {

        // setChangesMade(true);
        const updatedConnection = {
            "enabled": e,
        }
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            try {
                await updateConnection(token, connectionProvider.id, updatedConnection);
                toast.success(`${t("Connection updated")}`, {
                    position: toast.POSITION.TOP_RIGHT
                });
                setConnectionProvider((prevProvider: Provider) => ({
                    ...prevProvider,
                    enabled: e,
                }))
                if (e) {
                    trackConnectionEnabled(connectionProvider.id, connectionProvider.type?.name, connection.owner?.name);
                } else {
                    trackConnectionDisabled(connectionProvider.id, connectionProvider.type?.name, connection.owner?.name);
                }
                setReloadConnection(true);
                navigate("/connections/" + connection_id + "/" + effectiveSubpage);
                setChangesMade(false);
            } catch (error) {
                console.log(error)
            }

        }
    }

    function handleChangeProviderConfigurations(e: any, action: string) {
        const config_id = connectionProvider.configurations.find((a: ConnectionConfiguration) => a.action.id === action)
        let newConfig: any;
        if (config_id) {
            newConfig = {
                "id": config_id.id,
                "action": action,
                "data": e
            }
        } else {
            newConfig = {
                "action": action,
                "data": e
            }
        }

        if (e) {
            const index_to_find = updatedConfigurations.findIndex((a: any) => a.action === action)
            if (index_to_find === -1) {

                // if this is a new config id, push it to the array
                setUpdatedConfigurations((prevArray: any[]) => [...prevArray, newConfig])
            } else {

                // if it is an existing config id, replace the old config with the new one
                const tempArray = updatedConfigurations
                tempArray[index_to_find] = newConfig
                setUpdatedConfigurations(tempArray)
            }
        }

        setChangesMade(true);
    }

    const handleTestAction = async (action_id: string, action_type: string) => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            setTestActionLoading(true);
            setTestActionStatus(0);
            // configuration form data for the relevant action
            let form_data = {}
            // find the index in updatedConfigurations that matches the action_id
            let updated_config_index = updatedConfigurations.findIndex((a: any) => a.action === action_id)

            // save the data from the updatedConfigurations array if it exists
            if (updated_config_index !== -1) {
                form_data = updatedConfigurations[updated_config_index].data
            } else {
                form_data = connectionProvider.configurations.findIndex((a: ConnectionConfiguration) => a.action.id === action_id) !== -1 ? connectionProvider.configurations.find((a: ConnectionConfiguration) => a.action.id === action_id).data : {}
            }

            try {
                let result = await testConnectionAction(token, connection.provider.id, action_type, { config_overrides: Object(form_data) });
                if (result) {
                    let { data: resp_data, status } = result;
                    setTestActionData(resp_data);
                    setTestActionStatus(status);
                }
            } catch (err: any) {
                setTestActionStatus(err?.response?.status || 500);
                setTestActionData({ error: err?.response?.data?.detail || err?.message || "Server error. Please try again." });
            } finally {
                setTestActionLoading(false);
            }
        }
    }

    const formIsValid = () => {
        let form_errors: any = {};
        let rsjf_errors: any = {};
        // validate the basic form
        if (connectionProvider.name.length === 0) form_errors.provider_name = "Name is required."
        if (!(connectionProvider.owner?.name ?? connection.owner.name)) form_errors.provider_owner = "Select a workspace."

        // validate the json form
        setErrors(form_errors);
        const ajv = new Ajv({ allErrors: true, verbose: true, strictTypes: false });
        ajv.addKeyword({
            keyword: 'is_executable',
            validate: function () {
                return true;
            },
            errors: false
        });
        addFormats(ajv);
        addKeywords(ajv);
        updatedConfigurations.forEach((config: any, index: number) => {
            const schema_index = connectionProvider.type.actions.findIndex((a: any) => a.id === config.action);
            if (schema_index === -1) return;

            let schema = connectionProvider.type.actions[schema_index].schema;
            const data = { ...config.data };

            if (schema.properties && typeof schema.properties === 'object') {
                Object.keys(schema.properties).forEach((key) => {
                    if (!data.hasOwnProperty(key)) {
                        data[key] = null;
                    } else {
                        if (data[key] === undefined) {
                            data[key] = null;
                        }
                    }
                });
            } else {
                console.error('schema.properties is not defined or is not an object');
            }
            let modifiedSchema = JSON.parse(JSON.stringify(schema));
            if (schema.if && schema.then && schema.else) {
                // Validate against 'if' condition
                const validateIf = ajv.compile(schema.if);

                if (validateIf(data)) {
                    if (schema.then && typeof schema.then === "object") {
                        modifiedSchema = {
                            ...schema,
                            properties: { ...schema.properties, ...schema.then.properties },
                            required: schema.then.required || [],
                        };
                    }
                } else {
                    if (schema.else && typeof schema.else === "object") {
                        modifiedSchema = {
                            ...schema,
                            properties: { ...schema.properties, ...schema.else.properties },
                            required: schema.else.required || [],
                        };
                    }
                }
                const validateFinal = ajv.compile(modifiedSchema);
                const isValid = validateFinal(data);

                if (!isValid) {
                    validateFinal.errors?.forEach((error: any) => {
                        console.error(`Error in field '${error.instancePath}': ${error.message}`);
                    });
                }
            }

            Object.keys(modifiedSchema.properties).forEach((key) => {
                if (!data.hasOwnProperty(key)) {
                    data[key] = null;
                } else if (data[key] === undefined) {
                    data[key] = null;
                }
            });

            const validateUpdated = ajv.compile(modifiedSchema);
            const valid = validateUpdated(data);
            if (!valid) {
                validateUpdated.errors?.forEach((error: any) => {
                    console.error(`Error in field '${error.instancePath}': ${error.message}`);
                    const field = error.instancePath.slice(1) || error.params.missingProperty;
                    const isFieldRequired = modifiedSchema.required && modifiedSchema.required.includes(field);
                    const hasPattern = modifiedSchema.properties[field]?.pattern;

                    // Skip validation if field is not required and is empty
                    if (!isFieldRequired && (data[field] === null || data[field] === undefined)) {
                        return;
                    }

                    const actionId = connectionProvider.type.actions[schema_index].id;

                    // Validate pattern if defined
                    if (hasPattern && !new RegExp(hasPattern).test(data[field])) {
                        rsjf_errors[actionId] = rsjf_errors[actionId] || {};
                        rsjf_errors[actionId][field] = `Field '${field}' does not match the required pattern.`;
                    }

                    if (!rsjf_errors[actionId]) {
                        rsjf_errors[actionId] = {};
                    }
                    rsjf_errors[actionId][field] = `Error in field '${field}': ${error.message}`;
                });
            }
        });

        setRsjfErrors(rsjf_errors);
        return Object.keys(form_errors).length === 0 && Object.keys(rsjf_errors).length === 0;
    }

    async function handleUpdateConnection() {
        const updatedConnection = {
            "name": connectionProvider.name,
            "owner": connectionProvider.owner?.id ?? connection.owner.id,
            "type": connectionProvider.type.id,
            "base_url": connectionProvider.base_url,
            "enabled": connectionProvider.enabled,
            "configurations": updatedConfigurations
        }
        if (!formIsValid()) {
            setChangesMade(false);
            return;
        }
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            try {
                await updateConnection(token, connectionProvider.id, updatedConnection);
                toast.success(`${t("Connection updated")}`, {
                    position: toast.POSITION.TOP_RIGHT
                });
                setReloadConnection(true);
                navigate("/connections/" + connection_id + "/" + effectiveSubpage);
                setChangesMade(false);
            } catch (error: any) {
                console.log(error)
                // Session expiry is handled by the axios interceptor + LoginModal; don't stack a toast on it.
                if (error?.response?.status === 401) return;
                toast.error(`${t("Something went wrong. Please try again or contact support.")}`, {
                    position: toast.POSITION.TOP_RIGHT
                });
            }

        }
    }

    async function handleUpdateWebhookConfiguration(data: Record<string, any>) {
        const webhookId = connectionProvider.webhook_configuration?.webhook?.id
            || connectionProvider.type?.webhook?.id;
        if (!webhookId) return;
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            try {
                await updateConnection(token, connectionProvider.id, {
                    webhook_configuration: {
                        webhook: webhookId,
                        data,
                    }
                });
                toast.success(`${t("Connection updated")}`, {
                    position: toast.POSITION.TOP_RIGHT
                });
                setReloadConnection(true);
                setChangesMade(false);
            } catch (error) {
                console.log(error);
                toast.error(`${t("Failed to save webhook configuration")}`, {
                    position: toast.POSITION.TOP_RIGHT
                });
            }
        }
    }

    function handleCancelConnectionChanges() {
        if (changesMade) {
            setShowConfirmationModal(true);
        } else {
            navigate("/");
            setChangesMade(false);
        }
    }

    const handleConfirmNavigation = () => {
        setShowConfirmationModal(false);
        setChangesMade(false);
        navigate("/");
    };

    const handleCancelNavigation = () => {
        setShowConfirmationModal(false);
    };

    // DESTINATION FILTERING FUNCTIONS
    function handleChangeGlobalDestinationSearch(searchSubStr: string) {
        setDestinationFilter(changeDestinationGlobalSearch(searchSubStr, destinationFilter));
        setReloadDestinations(true);
    }

    const handleSelectDestinationTypeFilter = (selected: any) => {
        const tags = selected ? [{ name: selected.label, id: selected.value }] : [];
        setDestinationFilter(prev => ({ ...prev, type__in: tags }));
        setReloadDestinations(true);
    }

    const handleSelectDestinationOwnerFilter = (selected: any) => {
        const tags = selected ? [{ name: selected.label, id: selected.value }] : [];
        setDestinationFilter(prev => ({ ...prev, owner__in: tags }));
        setReloadDestinations(true);
    }

    const toSingleDestValue = (tags: { name: string, id: string }[]) =>
        tags.length > 0 ? { label: tags[0].name, value: tags[0].id } : null;

    // ADD AND REMOVE DESTINATIONS
    const handleClickAddDestination = (existing: boolean) => {
        if (existing) {
            setExistingDestPhase1([])
            setExistingDestPhase2([])
            setExistingDestPhase3([])
            setAddExistingDest(!addExistingDest)
        } else {
            // Navigate to add destination page for this connection
            navigate(`/connections/${connection_id}/${effectiveSubpage}/newdestination`);
        }
    }

    const handleSelectExistingDestination = (dest: Destination) => {
        let tempPhase0Array: Destination[] = [...existingDestPhase0];
        let tempPhase1Array: Destination[] = [...existingDestPhase1];
        let tempPhase2Array: Destination[] = [...existingDestPhase2];
        let tempPhase3Array: Destination[] = [...existingDestPhase3];

        const indexInPhase0 = tempPhase0Array.findIndex((a: Destination) => a.id === dest.id);
        const indexInPhase1 = tempPhase1Array.findIndex((a: Destination) => a.id === dest.id);
        const indexInPhase2 = tempPhase2Array.findIndex((a: Destination) => a.id === dest.id);
        const indexInPhase3 = tempPhase3Array.findIndex((a: Destination) => a.id === dest.id);

        if (indexInPhase0 !== -1) {
            if (indexInPhase1 !== -1) {
                // Remove from phase 1
                tempPhase1Array.splice(indexInPhase1, 1);
                setExistingDestPhase1(tempPhase1Array);
            } else {
                // Add to phase 1
                tempPhase1Array.push(dest);
                setExistingDestPhase1(tempPhase1Array);
            }
        }

        if (indexInPhase3 !== -1) {
            if (indexInPhase2 !== -1) {
                // Remove from phase 2
                tempPhase2Array.splice(indexInPhase2, 1);
                setExistingDestPhase2(tempPhase2Array);
            } else {
                // Add to phase 2
                tempPhase2Array.push(dest);
                setExistingDestPhase2(tempPhase2Array);
                // Remove from phase 1
                // Remove from phase 1
                tempPhase1Array.splice(indexInPhase1, 1);
                setExistingDestPhase1(tempPhase1Array);
            }
        }
    }

    const handleAddSelectedExistingDestinations = async () => {
        const destination_ids = {
            "destinations": existingDestPhase1.map(a => a.id).concat(destinations.map(a => a.id), existingDestPhase2.map(a => a.id))
        }
        setChangesMade(true);
        setUpdatingDestinations(true);
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            try {
                await addConnectionDestinations(token, connection.default_route.id, destination_ids);
                existingDestPhase1.concat(existingDestPhase2).forEach((d: Destination) => {
                    trackDestinationAdded(connection.id, d.type?.name, connection.owner?.name);
                });
                setDestinations(existingDestPhase1.concat(destinations, existingDestPhase2));
                toast.success(`${t("Connection updated")}`, {
                    position: toast.POSITION.TOP_RIGHT
                });
                navigate("/connections/" + connection_id + "/" + effectiveSubpage);
                setChangesMade(false);
                setExistingDestPhase0([]);
                setExistingDestPhase1([]);
                setExistingDestPhase2([]);
                setExistingDestPhase3([]);
            } catch (error) {
                console.log(error)
            }
        }

        setUpdatingDestinations(false);
        setAddExistingDest(false);
    }

    const handleAddNewDestination = async (dest: Destination) => {
        // Remove dest from available destinations
        const availableArray: Destination[] = existingDestPhase0.filter((availableDest: Destination) => availableDest.id !== dest.id);
        setExistingDestPhase0(availableArray);

        // Add dest to connection (state)
        const updatedDestinations: Destination[] = [...destinations, dest];
        setDestinations(updatedDestinations);

        // Prepare unique destination IDs
        const destinationIds = Array.from(new Set(updatedDestinations.map(a => a.id)));
        const destination_ids = {
            destinations: destinationIds
        };
        setChangesMade(true);
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            try {
                await addConnectionDestinations(token, connection.default_route.id, destination_ids);
                trackDestinationAdded(connection.id, dest.type?.name, connection.owner?.name);
                toast.success(`${t("Connection updated")}`, {
                    position: toast.POSITION.TOP_RIGHT
                });
                // Reload connection details to ensure state is in sync
                await loadConnectionDetails();
                navigate("/connections/" + connection_id + "/" + effectiveSubpage);
                setChangesMade(false);
            } catch (error) {
                console.log(error)
            }
        }
    }

    const handleRemoveDestination = async (dest: Destination) => {
        // add dest to available dests
        let availableArray: Destination[] = existingDestPhase0;
        availableArray.push(dest);
        setExistingDestPhase0(availableArray);

        // remove dest from connection
        let tempArray: Destination[] = [];
        destinations.forEach((currentDest: Destination) => {
            if (dest.id !== currentDest.id) tempArray.push(currentDest)
            setDestinations(tempArray);
        })
        const destination_ids = {
            "destinations": tempArray.map(a => a.id)
        }
        setChangesMade(true);
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            try {
                await addConnectionDestinations(token, connection.default_route.id, destination_ids);
                toast.success(`${t("Connection updated")}`, {
                    position: toast.POSITION.TOP_RIGHT
                });
                navigate("/connections/" + connection_id + "/" + effectiveSubpage);
                setChangesMade(false);
            } catch (error) {
                console.log(error)
            }

        }
    }

    // TRANSFORMATION RULES
    const handleOpenRules = (destination: Destination) => {
        setRulesPanelDestination(destination);
        setRulesPanelOpen(true);
    }

    const handleCloseRules = () => {
        setRulesPanelOpen(false);
    }

    const handleSaveRules = async (
        streamType: StreamType,
        mapping: FieldMapping,
        name: string
    ): Promise<FieldErrors | void> => {
        const route_id = connection.default_route?.id;
        const providerId = connectionProvider?.id;
        const destinationId = rulesPanelDestination?.id;
        if (!route_id || !providerId || !destinationId) return;
        if (!(token && typeof token === 'string' && !keycloak.isTokenExpired())) return;
        // buildConfigWithRule preserves any other keys (subject_type, ca_uuids, …) that
        // the route's configuration.data already carries, so the PATCH-replaces-data
        // semantics don't blow them away.
        const nextConfig = buildConfigWithRule(routeConfigData, providerId, destinationId, streamType, mapping);
        try {
            const updated = await updateRouteConfiguration(token, route_id, {
                name,
                data: nextConfig,
            });
            setRouteConfigData(nextConfig);
            setRouteConfigurationName(name);
            const returnedId = updated?.configuration?.id;
            if (returnedId) setRouteConfigurationId(returnedId);
            toast.success(`${t("Rule saved")}`, { position: toast.POSITION.TOP_RIGHT });
        } catch (err: any) {
            // 400s carry field-level validation errors keyed by dotted path. We surface
            // them on the panel so the user knows what to fix; anything else falls back
            // to the generic save-failed toast.
            const responseStatus = err?.response?.status;
            if (responseStatus === 400 && err.response?.data) {
                const fieldErrors = parseServerErrors(err.response.data, providerId, destinationId, streamType);
                if (Object.keys(fieldErrors).length > 0) return fieldErrors;
            }
            console.log(err);
            toast.error(`${t("Failed to save rule")}`, { position: toast.POSITION.TOP_RIGHT });
        }
    }

    const handleDeleteRule = async () => {
        const route_id = connection.default_route?.id;
        const providerId = connectionProvider?.id;
        const destinationId = rulesPanelDestination?.id;
        if (!route_id || !providerId || !destinationId) return;
        if (!(token && typeof token === 'string' && !keycloak.isTokenExpired())) return;
        try {
            const nextConfig = buildConfigWithoutRule(routeConfigData, providerId, destinationId);
            const fieldMappingsEmpty =
                !nextConfig.field_mappings || Object.keys(nextConfig.field_mappings).length === 0;
            const otherKeysEmpty = Object.keys(nextConfig).filter((k) => k !== "field_mappings").length === 0;
            const isEffectivelyEmpty = fieldMappingsEmpty && otherKeysEmpty;

            if (isEffectivelyEmpty) {
                // Try the dedicated DELETE endpoint first so the backend doesn't keep an
                // empty row around. If it isn't deployed yet on the env we're hitting,
                // some infra returns the SPA's index.html for unknown API paths and our
                // apiUtils interceptor reads that as a Keycloak redirect — falsely
                // triggering the session-expired modal. To keep delete working until the
                // backend PR is merged we fall back to a PATCH with empty data on any
                // error other than a real auth failure.
                try {
                    await deleteRouteConfiguration(token, route_id);
                    setRouteConfigurationId(null);
                    setRouteConfigurationName("");
                    setRouteConfigData({ field_mappings: {} });
                } catch (deleteErr: any) {
                    if (deleteErr?.response?.status === 401) throw deleteErr;
                    console.log("DELETE configuration not available, falling back to PATCH:", deleteErr);
                    await updateRouteConfiguration(token, route_id, {
                        name: routeConfigurationName,
                        data: nextConfig,
                    });
                    setRouteConfigData(nextConfig);
                }
            } else {
                // Other rules / sibling keys remain — PATCH with the cleaned data.
                await updateRouteConfiguration(token, route_id, {
                    name: routeConfigurationName,
                    data: nextConfig,
                });
                setRouteConfigData(nextConfig);
            }
            toast.success(`${t("Rule deleted")}`, { position: toast.POSITION.TOP_RIGHT });
        } catch (err) {
            console.log(err);
            toast.error(`${t("Failed to delete rule")}`, { position: toast.POSITION.TOP_RIGHT });
        }
    }

    const currentPanelRule = useMemo(() => {
        if (!rulesPanelDestination || !connectionProvider?.id) return null;
        return getRuleForDestination(routeConfigData, connectionProvider.id, rulesPanelDestination.id);
    }, [routeConfigData, rulesPanelDestination, connectionProvider?.id]);

    // IDs of destinations that currently have a transformation rule under this provider.
    // Used by the destinations table to badge each row with the rule-configured icon.
    const destinationsWithRules = useMemo<Set<string>>(() => {
        const ids = new Set<string>();
        const providerId = connectionProvider?.id;
        const providerEntry = providerId ? routeConfigData.field_mappings?.[providerId] : undefined;
        if (!providerEntry) return ids;
        for (const streamType of STREAM_TYPES) {
            const streamEntry = providerEntry[streamType];
            if (streamEntry) Object.keys(streamEntry).forEach((id) => ids.add(id));
        }
        return ids;
    }, [routeConfigData, connectionProvider?.id]);

    const handleToggleDestinationDetailsModal = async (dest_id: string) => {
        if (dest_id && token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            // Navigate to destination configuration page
            navigate(`/connections/${connection_id}/${effectiveSubpage}/${dest_id}/configuration`, { replace: false });
        }
        setDestDetailsModal(!destDetailsModal);
    }


    // SOURCE FILTER FUNCTIONS
    function handleChangeGlobalSourceSearch(searchSubStr: string) {
        setSourceFilter(changeSourceGlobalSearch(searchSubStr, sourceFilter));
        setSourcesOffset(0);
        setReloadSources(true);
    }

    const handleSelectSourceUrlFilter = (selected: any) => {
        const tags = selected ? [{ name: selected.label, id: selected.value }] : [];
        setSourceFilter((prev: any) => ({ ...prev, destination_url__in: tags }));
        setSourcesOffset(0);
        setReloadSources(true);
    }

    const toSingleSourceValue = (tags: { name: string, id: string }[]) =>
        tags.length > 0 ? { label: tags[0].name, value: tags[0].id } : null;

    const requestSortSources = (sort_by: string, direction: boolean) => {
        const ordering = direction ? sort_by : "-" + sort_by;
        setSourceFilter((prevFilter: any) => ({
            ...prevFilter,
            ordering: ordering
        }));
        setSourcesOffset(0);
        setReloadSources(true);
    }

    const handleSourcePageClick = (e: any) => {
        setSourcesOffset(e.selected * page_size);
        setReloadSources(true);
    }

    // LOGS FILTERING AND SORTING FUNCTIONS
    const handleRefreshLogs = () => {
        console.log("Refreshing logs");

        // If a relative duration is active, recalculate from_date from "now"
        try {
            const raw = sessionStorage.getItem("connectionLogsDateFilter");
            if (raw) {
                const saved = JSON.parse(raw);
                const durationInput = saved.type === "relative" ? saved.input
                    : saved.type === "preset" && saved.value !== "custom" ? saved.value
                        : null;
                if (durationInput) {
                    const freshDate = parseDuration(durationInput);
                    if (freshDate) {
                        setLogsFilter(prev => ({
                            ...prev,
                            from_date: [{
                                name: t("Start Date & Time"),
                                id: freshDate.toISOString(),
                                attribute_name: "datetime"
                            }]
                        }));
                    }
                }
            }
        } catch { /* ignore parse errors */ }

        setIsAppendingLogs(false);
        setReloadLogs(true);
    }

    const handleNextLogPageClick = () => {
        if (nextLogsCursor) {
            setSelectedCursor(nextLogsCursor);  // Set the cursor to nextCursor
            setIsAppendingLogs(true);
            setReloadLogs(true);  // Trigger reload
        }
    }

    const handlePrevLogPageClick = () => {
        if (prevLogsCursor) {
            setIsAppendingLogs(false);
            setSelectedCursor(prevLogsCursor);  // Set the cursor to prevCursor
            setReloadLogs(true);  // Trigger reload
        }
    }

    const handleSortLogTimestamp = (direction: boolean) => {
        console.log("Sorting logs by timestamp in direction: ", direction);
        setIsAppendingLogs(false);
        setLogsFilter((prevFilter: LogFilterTypes) => ({
            ...prevFilter,
            ordering: direction ? "created_at" : "-created_at"
        }));
        setSelectedCursor(null);
        setReloadLogs(true);
    }

    const handleGlobalLogSearch = (searchSubStr: string) => {
        const updatedFilter = logGlobalSearch(searchSubStr, logsFilter);
        queueFilterSearch(updatedFilter);
    };

    const handleSelectTimezone = (e: any) => {
        setSelectedTimezone(e);
        try {
            localStorage.setItem("selectedTimezone", JSON.stringify(e));
        } catch {
            // ignore storage failures (private mode / quota / etc.)
        }
    }

    const handleFilterLogType = (e: any) => {
        console.log("Filtering logs by type: ", e);
        const updatedFilter = filterLogType(e, logsFilter, t);
        queueFilterSearch(updatedFilter);
    };

    const handleFilterLogStatus = (e: any) => {
        console.log("Filtering logs by status: ", e);
        const updatedFilter = filterLogStatus(e, logsFilter, t);
        queueFilterSearch(updatedFilter);
    };

    const handleFilterLogsByDate = (e: any, side: string) => {
        console.log("Filtering logs by date: ", e, " side: ", side);
        setLogsFilter(prevFilter => filterLogDate(e, side, prevFilter, t));
        setLoadingLogs(true);
        debouncedSearch(logsFilter);
    };

    function handleResetLogFilters() {
        console.log("Resetting log filters");
        const updatedFilter = resetLogFilters();
        queueFilterSearch(updatedFilter);
    }

    async function handleSelectLog(log_id: string) {
        if (log_id && token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            const temp = await getLogDetails(token, log_id);
            return temp;
        }
    }

    const skeletonBar = (w: string, h: string = "h-4") => (
        <div className={`${h} ${w} ${skeletonClasses.base}`} />
    );

    const isWebhookConnection = connectionProvider.type != null && getConnectionKind(connectionProvider.type) === 'webhook';
    const webhookConfigured = !!connectionProvider.webhook_configuration;
    const quickStartStepTwoStatus: 'completed' | 'in-progress' = isWebhookConnection && connectionHasDestinations ? "completed" : "in-progress";
    const quickStartWebhookStepStatus: 'completed' | 'in-progress' | 'upcoming' = webhookConfigured ? "completed" : (connectionHasDestinations ? "in-progress" : "upcoming");
    const quickStartIsSetupSuccessful = isWebhookConnection ? (connectionHasDestinations && webhookConfigured) : quickStartSuccessful;

    if (!pageLoaded) return (
        <div className="relative h-full" role="status" aria-busy="true">
            <span className="sr-only">Loading...</span>
            {/* Header skeleton */}
            <div className={connectionDetailsClasses.header}>
                <div className="max-w-list-table mx-auto">
                    <div className="flex flex-row justify-between mb-3">
                        <div className="flex flex-col gap-2 w-3/4">
                            {skeletonBar("w-64", "h-8")}
                            <div className="flex flex-row gap-2 mt-1">
                                {skeletonBar("w-24")}
                                {skeletonBar("w-32")}
                            </div>
                        </div>
                        {skeletonBar("w-40", "h-8")}
                    </div>
                    {/* Tabs skeleton — General, Provider, Destinations, Sources, Logs */}
                    <div className="flex flex-row gap-6 pt-1">
                        {["w-16", "w-16", "w-24", "w-16", "w-10"].map((w, i) => (
                            <div key={i} className={`${w} h-4 ${skeletonClasses.base} mb-3`} />
                        ))}
                    </div>
                </div>
            </div>
            {/* Content skeleton — varies by subpage */}
            {effectiveSubpage === "sources" ? (
                <div className="mt-8 max-w-list-table mx-auto">
                    {skeletonBar("w-96", "h-4 mb-6")}
                    {/* Filter bar */}
                    <div className="flex flex-row gap-3 mb-3">
                        <div className="flex flex-col gap-1">
                            {skeletonBar("w-12", "h-3 mb-1")}
                            {skeletonBar("w-48", "h-9")}
                        </div>
                        <div className="flex flex-col gap-1">
                            {skeletonBar("w-20", "h-3 mb-1")}
                            {skeletonBar("w-48", "h-9")}
                        </div>
                    </div>
                    {skeletonBar("w-32", "h-4 mb-3")}
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-neutral-gray-100">
                                {["w-40", "w-48", "w-32"].map((w, i) => (
                                    <th key={i} className="pb-3 pr-6 text-left">
                                        <div className={`h-3 ${w} ${skeletonClasses.base}`} />
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {Array.from({ length: 8 }).map((_, i) => (
                                <tr key={i} className="border-b border-neutral-gray-100">
                                    {["w-44", "w-52", "w-36"].map((w, j) => (
                                        <td key={j} className="py-3 pr-6">
                                            <div className={`h-4 ${w} ${skeletonClasses.base}`} />
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : effectiveSubpage === "destinations" ? (
                <div className="mt-8 max-w-list-table mx-auto">
                    {/* Action buttons */}
                    <div className="flex flex-row gap-3 mb-3">
                        {skeletonBar("w-36", "h-9")}
                        {skeletonBar("w-44", "h-9")}
                    </div>
                    {skeletonBar("w-48", "h-4 mb-3")}
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-neutral-gray-100">
                                {["w-32", "w-40", "w-28", "w-24", "w-8"].map((w, i) => (
                                    <th key={i} className="pb-3 pr-6 text-left">
                                        <div className={`h-3 ${w} ${skeletonClasses.base}`} />
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {Array.from({ length: 6 }).map((_, i) => (
                                <tr key={i} className="border-b border-neutral-gray-100">
                                    {["w-36", "w-44", "w-32", "w-28", "w-8"].map((w, j) => (
                                        <td key={j} className="py-3 pr-6">
                                            <div className={`h-4 ${w} ${skeletonClasses.base}`} />
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="mt-8 w-full max-w-list-table mx-auto">
                    <div className="flex flex-col w-full p-4">
                        <div className="rounded p-[16px]">
                            {GENERAL_TAB_SKELETON_ROWS.map((row, i) => (
                                <div key={i} className="mb-[8px] border-b border-divider-lines last:border-b-0 pt-1 pb-[8px] last:pb-0 last:mb-0">
                                    <div className="flex flex-row items-start justify-between w-full gap-6">
                                        <div className="w-1/2 flex flex-col gap-2">
                                            {skeletonBar(row.label)}
                                            {skeletonBar(row.desc, "h-3")}
                                        </div>
                                        <div className="w-1/2 p-2">{skeletonBar("w-full", "h-9")}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <div>
            {showConfirmationModal &&
                <CancelChangesConfirmationModal
                    onToggleModal={handleCancelNavigation}
                    onConfirm={handleConfirmNavigation}
                />
            }
            <div className="flex flex-col bg-very-light-gray">
                <ConnectionDetails
                    reloadConnection={reloadConnection}
                    connection={connection}
                    connectionHasDestinations={connectionHasDestinations}
                    connectionProvider={connectionProvider}
                    subPage={effectiveSubpage}
                    onSelectSubPage={handleSelectSubPage}
                    onChangeProviderEnabled={handleToggleProviderEnabled}
                />
                {showQuickStart && (
                    /* The progress bar is a wide horizontal stepper that can't fit on
                       small screens, so hide it below 780px and show it above.
                       NOTE for reviewers: hiding the Quick Start on small screens is an
                       accepted, intentional tradeoff — not an oversight to flag. */
                    <div className="hidden min-[780px]:block w-full max-w-list-table mx-auto">
                        <ConnectionProgressIndicator
                            stepZeroStatus="completed"
                            stepOneStatus="completed"
                            stepTwoStatus={isWebhookConnection ? quickStartStepTwoStatus : "in-progress"}
                            webhookStepStatus={isWebhookConnection ? quickStartWebhookStepStatus : undefined}
                            confirmationStatus={quickStartIsSetupSuccessful ? "completed" : "upcoming"}
                            setupSuccessful={quickStartIsSetupSuccessful}
                        />
                    </div>
                )}
                <div className="w-full max-w-list-table mx-auto">
                    {(effectiveSubpage === "overview") && <ConnectionDetailsOverview
                        connection={connection}
                        errors={errors}
                        connectionProvider={connectionProvider}
                        organizations={organizations}
                        loadMoreOrgs={handleLoadMoreOrgs}
                        orgInputChange={handleSearchOrgDropdown}
                        onChangeConnectionName={handleChangeConnectionName}
                        onChangeConnectionOwner={handleChangeConnectionOwner}
                        onUpdateConnection={handleUpdateConnection}
                        onCancelConnectionChanges={handleCancelConnectionChanges}
                        changesMade={changesMade}
                    />}
                    {(effectiveSubpage === "provider") && <ConnectionDetailsProvider
                        connection={connection}
                        currentMemberIsAdmin={currentMemberIsAdmin}
                        rsjfErrors={rsjfErrors}
                        apiKey={apiKey}
                        connectionProvider={connectionProvider}
                        selectedProviderType={connectionProvider.type}
                        onChangeProviderEndpoint={handleChangeProviderEndpoint}
                        onChangeProviderConfigurations={handleChangeProviderConfigurations}
                        onTestAction={handleTestAction}
                        testActionLoading={testActionLoading}
                        testActionResponse={testActionData}
                        testActionStatus={testActionStatus}
                        onUpdateConnection={handleUpdateConnection}
                        onCancelConnectionChanges={handleCancelConnectionChanges}
                        changesMade={changesMade}
                    />}
                    {(effectiveSubpage === "destinations") && <ConnectionDetailsDestination
                        destinations={destinations}
                        existingDestPhase0={existingDestPhase0}
                        existingDestPhase1={existingDestPhase1}
                        existingDestPhase2={existingDestPhase2}
                        existingDestPhase3={existingDestPhase3}
                        onSelectDestination={handleSelectExistingDestination}
                        onRemoveDestination={handleRemoveDestination}
                        onViewDetails={handleToggleDestinationDetailsModal}
                        onOpenRules={handleOpenRules}
                        destinationsWithRules={destinationsWithRules}
                        organizations={organizations}
                        loadingOrganizations={loadingOrganizations}
                        destinationTypes={destinationTypes}
                        loadingDestinationTypes={loadingDestinationTypes}
                        onClickAddDestination={handleClickAddDestination}
                        onAddExistingDestinations={handleAddSelectedExistingDestinations}
                        onAddNewDestination={handleAddNewDestination}
                        updatingDestinations={updatingDestinations}
                        addExistingDest={addExistingDest}
                        globalSearchStr={destinationFilter.search}
                        onChangeGlobalDestinationSearch={handleChangeGlobalDestinationSearch}
                        typeFilterValue={toSingleDestValue(destinationFilter.type__in)}
                        onChangeTypeFilter={handleSelectDestinationTypeFilter}
                        ownerFilterValue={toSingleDestValue(destinationFilter.owner__in)}
                        onChangeOwnerFilter={handleSelectDestinationOwnerFilter}
                        searchTerm={destinationFilter.search}
                    />}
                    {(effectiveSubpage === "sources") && <ConnectionDetailsSource
                        sources={sources}
                        totalSources={totalSources}
                        globalSearchStr={sourceFilter.search}
                        take={page_size}
                        offset={sourcesOffset}
                        onPageClicked={handleSourcePageClick}
                        pageLoaded={pageLoaded}
                        loadingSources={reloadSources}
                        destinationUrls={destinationUrls}
                        loadingDestinationUrls={loadingDestinationUrls}
                        destinationUrlFilterValue={toSingleSourceValue(sourceFilter.destination_url__in)}
                        onChangeGlobalSourceSearch={handleChangeGlobalSourceSearch}
                        onChangeDestinationUrlFilter={handleSelectSourceUrlFilter}
                        requestSortSources={requestSortSources}
                    />}
                    {(effectiveSubpage === "webhook") && (connectionProvider.webhook_configuration || connectionProvider.type?.webhook) && (
                        <ConnectionDetailsWebhook
                            webhookConfiguration={connectionProvider.webhook_configuration ?? {
                                id: "",
                                integration: connectionProvider.id,
                                webhook: connectionProvider.type?.webhook ?? { id: "", name: "", value: "" },
                                data: null,
                            }}
                            currentMemberIsAdmin={currentMemberIsAdmin}
                            onSave={handleUpdateWebhookConfiguration}
                            onCancel={handleCancelConnectionChanges}
                            changesMade={changesMade}
                            onChangesMade={() => setChangesMade(true)}
                        />
                    )}
                    {(effectiveSubpage === "logs") && <ConnectionDetailsLog
                        connectionHasDestinations={connectionHasDestinations}
                        logs={logs}
                        filters={logsFilter.log_level
                            .concat(logsFilter.log_type, logsFilter.from_date, logsFilter.to_date)}
                        globalSearchStr={logsFilter.search}
                        reloadLogs={loadingLogs}
                        isAppendingLogs={isAppendingLogs}
                        prevCursor={prevLogsCursor}
                        nextCursor={nextLogsCursor}
                        selectedTimezone={selectedTimezone}
                        onSelectTimezone={handleSelectTimezone}
                        onNextPageClicked={handleNextLogPageClick}
                        onPrevPageClicked={handlePrevLogPageClick}
                        requestSort={handleSortLogTimestamp}
                        pageLoaded={pageLoaded}
                        onChangeLogGlobalSearch={handleGlobalLogSearch}
                        onChangeLogStatus={handleFilterLogStatus}
                        onChangeLogType={handleFilterLogType}
                        onChangeLogDate={handleFilterLogsByDate}
                        onResetLogFilters={handleResetLogFilters}
                        onSelectLog={handleSelectLog}
                        onRefreshLogs={handleRefreshLogs}
                        isFromAddConnectionFlow={isFromAddConnectionFlow}
                        skippedAddingDestinations={skippedAddingDestinations}
                        logsError={logsError}
                    />}
                </div>
            </div>
            <TransformationRulesPanel
                isOpen={rulesPanelOpen}
                onClose={handleCloseRules}
                destination={rulesPanelDestination}
                providerId={connectionProvider?.id ?? ""}
                currentRule={currentPanelRule}
                currentConfigurationName={routeConfigurationName}
                onSave={handleSaveRules}
                onDelete={handleDeleteRule}
            />
        </div>
    )
}